import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ClockCounterClockwise, ArrowUpRight, Trash, MagnifyingGlass, X,
  Warning,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useI18n } from "@/context/I18nContext";

// ---------------------------------------------------------------------------
// History page — one row per past discovery session. Clicking a row opens
// the session detail with the leads that session discovered. Deleting a row
// cascades to the leads (only leads that no other session references).
// ---------------------------------------------------------------------------
export default function History() {
  const { locale } = useI18n();
  const t = (tr, en) => (locale === "en" ? en : tr);
  const nav = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/history/sessions", { params: { limit: 100 } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(locale === "en" ? "Failed to load history" : "Geçmiş yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [locale]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((s) => {
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (s.raw_goal || "").toLowerCase().includes(needle);
  });

  const doDelete = async () => {
    const s = pendingDelete;
    setPendingDelete(null);
    if (!s) return;
    try {
      const { data } = await api.delete(`/agent/sessions/${s.id}`);
      setItems((prev) => prev.filter((x) => x.id !== s.id));
      toast.success(
        t(
          `Silindi — ${data.deleted_leads || 0} kayıt kaldırıldı`,
          `Deleted — ${data.deleted_leads || 0} lead(s) removed`
        )
      );
    } catch (e) {
      toast.error(t("Silinemedi", "Delete failed"));
    }
  };

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-[1300px]">
      <div>
        <div className="acas-eyebrow mb-2">
          <ClockCounterClockwise size={11} weight="fill" className="inline mr-1" />
          {t("Keşif Geçmişi", "Discovery History")}
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="history-title">
          {t("Geçmiş Aramalar", "Past Searches")}
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          {t(
            "Her satır, geçmişte başlattığınız bir keşif oturumunu ve o arama sırasında bulunan kayıtları temsil eder. Panelinizde aktif arama yalnızca son başlattığınız olur; geçmiş buradan yönetilir.",
            "Each row represents a past discovery run and the leads it found. Your dashboard only shows the run you're actively working on; older runs live here."
          )}
        </p>
      </div>

      {/* Search bar */}
      <div className="rounded-lg border border-border/60 bg-card p-4">
        <div className="relative flex-1 min-w-0">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="history-search"
            placeholder={t("Aramada geçen bir kelime yazın…", "Filter by search text…")}
            className="pl-9 pr-9"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              data-testid="history-search-clear"
              aria-label={t("Aramayı temizle", "Clear search")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition"
            >
              <X size={14} weight="bold" />
            </button>
          )}
        </div>
      </div>

      {/* Sessions list */}
      <div className="space-y-3">
        {loading && [1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 w-full rounded-lg" />
        ))}
        {!loading && filtered.length === 0 && (
          <div className="rounded-lg border border-border/60 bg-card p-8 text-center text-sm text-muted-foreground">
            {q
              ? t("Bu filtreyle eşleşen bir geçmiş kaydı yok.", "No history matches this filter.")
              : t(
                  "Henüz bir keşif çalıştırmadınız. Kontrol paneline gidin ve bir arama başlatın.",
                  "You haven't run a discovery yet. Head to the Dashboard and start a search."
                )}
          </div>
        )}
        {!loading && filtered.map((s) => (
          <SessionRow
            key={s.id}
            session={s}
            t={t}
            locale={locale}
            onOpen={() => nav(`/gecmis/${s.id}`)}
            onDelete={() => setPendingDelete(s)}
          />
        ))}
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="delete-session-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-rose-500" />
              {t("Bu aramayı sil?", "Delete this search?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "Bu keşif çalışmasını ve keşfettiği kayıtları kalıcı olarak siler. Başka bir aramada da geçen kayıtlar korunur.",
                "Deletes this discovery run and the leads it discovered. Leads that were also found by another run stay intact."
              )}
              {pendingDelete && (
                <div className="mt-3 text-xs mono px-2 py-1.5 rounded bg-muted/60 border border-border/50">
                  “{pendingDelete.raw_goal}”
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-session-cancel">
              {t("Vazgeç", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="delete-session-confirm"
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-500"
            >
              {t("Sil", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SessionRow({ session, t, locale, onOpen, onDelete }) {
  const started = new Date(session.started_at || session.updated_at || Date.now());
  const dateFmt = started.toLocaleDateString(locale === "en" ? "en-US" : "tr-TR", {
    day: "2-digit", month: "short", year: "numeric",
  });
  const count = session.result_count ?? session.counts?.total ?? 0;
  const dropped = session.dropped_off_topic || 0;
  const providers = session.plan?.providers || [];
  return (
    <div
      data-testid={`session-row-${session.id}`}
      className="rounded-lg border border-border/60 bg-card p-4 hover:border-primary/50 transition cursor-pointer flex items-start gap-4"
      onClick={onOpen}
    >
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">
          {session.raw_goal || t("(başlıksız arama)", "(untitled search)")}
        </div>
        <div className="text-xs text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="mono">{dateFmt}</span>
          <span>·</span>
          <span data-testid={`session-count-${session.id}`}>
            <strong className="text-foreground">{count}</strong> {t("kayıt", "leads")}
          </span>
          {dropped > 0 && (
            <>
              <span>·</span>
              <span title={t("İlgisiz olarak elenen kayıt sayısı", "Off-topic results filtered")}>
                {dropped} {t("elenen", "filtered")}
              </span>
            </>
          )}
          {providers.length > 0 && (
            <>
              <span>·</span>
              {providers.map((p) => (
                <Badge key={p} variant="outline" className="font-normal text-[10px] px-1.5 py-0">
                  {p}
                </Badge>
              ))}
            </>
          )}
          {session.state === "error" && (
            <Badge variant="outline" className="border-rose-500/40 text-rose-600 dark:text-rose-400">
              {t("Hata", "Error")}
            </Badge>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          size="icon"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          data-testid={`session-delete-${session.id}`}
          aria-label={t("Sil", "Delete")}
        >
          <Trash size={16} />
        </Button>
        <Button size="icon" variant="ghost" tabIndex={-1}>
          <ArrowUpRight size={16} />
        </Button>
      </div>
    </div>
  );
}
